import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Target, Trophy, Zap, Timer } from 'lucide-react';
import playerManager from '../../utils/playerManager';

const TapChallenge = ({ onGameComplete, gameConfig }) => {
  const [gameState, setGameState] = useState('waiting'); // waiting, playing, finished
  const [score, setScore] = useState(0);
  const [timeLeft, setTimeLeft] = useState(gameConfig.duration);
  const [targets, setTargets] = useState([]);
  const [targetCount, setTargetCount] = useState(0);
  const [missedTargets, setMissedTargets] = useState(0);
  
  // Generate random target
  const generateTarget = useCallback(() => {
    const id = Date.now() + Math.random();
    const x = Math.random() * 80 + 5; // 5-85% from left
    const y = Math.random() * 70 + 15; // 15-85% from top
    const points = Math.floor(Math.random() * 50) + 50; // 50-100 points
    const lifetime = 2000 + Math.random() * 1000; // 2-3 seconds
    
    const target = {
      id,
      x,
      y,
      points,
      created: Date.now()
    };
    
    setTargets(prev => [...prev, target]);
    
    // Remove target after lifetime
    setTimeout(() => {
      setTargets(prev => {
        const updated = prev.filter(t => t.id !== id);
        if (prev.find(t => t.id === id)) {
          setMissedTargets(m => m + 1);
        }
        return updated;
      });
    }, lifetime);
  }, []);
  
  // Start game
  const startGame = () => {
    setGameState('playing');
    setScore(0);
    setTimeLeft(gameConfig.duration);
    setTargets([]);
    setTargetCount(0);
    setMissedTargets(0);
  };
  
  // Game timer
  useEffect(() => {
    if (gameState === 'playing' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(time => time - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (gameState === 'playing' && timeLeft === 0) {
      endGame();
    }
  }, [gameState, timeLeft]);
  
  // Generate targets during game
  useEffect(() => {
    if (gameState === 'playing') {
      const interval = setInterval(() => {
        if (targets.length < 3) { // Max 3 targets at once
          generateTarget();
        }
      }, 800 + Math.random() * 400); // 0.8-1.2 seconds
      
      return () => clearInterval(interval);
    }
  }, [gameState, targets.length, generateTarget]);
  
  // Hit target
  const hitTarget = (targetId) => {
    const target = targets.find(t => t.id === targetId);
    if (!target) return;
    
    const reactionTime = Date.now() - target.created;
    const speedBonus = Math.max(0, 1000 - reactionTime) / 10; // Bonus for fast reactions
    const finalScore = Math.floor(target.points + speedBonus);
    
    setScore(prev => prev + finalScore);
    setTargetCount(prev => prev + 1);
    setTargets(prev => prev.filter(t => t.id !== targetId));
  };
  
  // End game
  const endGame = async () => {
    setGameState('finished');
    setTargets([]);
    
    const accuracy = targetCount > 0 ? (targetCount / (targetCount + missedTargets) * 100) : 0;
    const gameData = {
      targetsHit: targetCount,
      targetsMissed: missedTargets,
      accuracy: parseFloat(accuracy.toFixed(1)),
      finalScore: score,
      completion_time: gameConfig.duration * 1000 - (timeLeft * 1000)
    };
    
    // Submit score to backend
    try {
      await playerManager.submitScore('tap-challenge', 'Tap Challenge', score, gameData);
    } catch (error) {
      console.error('Failed to submit score:', error);
    }
    
    setTimeout(() => {
      onGameComplete(score, gameData);
    }, 3000);
  };
  
  if (gameState === 'waiting') {
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Target className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Tap Challenge</CardTitle>
            <p className="text-gray-400">
              Click the targets as fast as you can! Each target has different point values.
            </p>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration:</span>
                <span className="text-white">{gameConfig.duration} seconds</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Entry Fee:</span>
                <span className="text-yellow-400 font-semibold">₹10</span>
              </div>
            </div>
            <Button onClick={startGame} className="w-full btn-primary py-3 text-lg">
              Start Game
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (gameState === 'finished') {
    const accuracy = targetCount > 0 ? (targetCount / (targetCount + missedTargets) * 100) : 0;
    
    return (
      <div className="text-center">
        <Card className="gaming-card max-w-lg mx-auto p-8">
          <CardHeader className="pb-6">
            <Trophy className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <CardTitle className="text-2xl text-white">Game Complete!</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 mb-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{score}</div>
                <div className="text-gray-400">Final Score</div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-green-400">{targetCount}</div>
                  <div className="text-gray-400 text-sm">Targets Hit</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-blue-400">{accuracy.toFixed(1)}%</div>
                  <div className="text-gray-400 text-sm">Accuracy</div>
                </div>
              </div>
            </div>
            
            <div className="text-gray-400 text-sm">
              Returning to games lobby in 3 seconds...
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="relative">
      {/* Game HUD */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex space-x-4">
          <Badge className="bg-yellow-400/20 text-yellow-400 border-yellow-400/30 px-4 py-2">
            Score: {score}
          </Badge>
          <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 px-4 py-2">
            Hits: {targetCount}
          </Badge>
        </div>
        <Badge className="bg-red-500/20 text-red-400 border-red-500/30 px-4 py-2 flex items-center space-x-2">
          <Timer className="w-4 h-4" />
          <span>{timeLeft}s</span>
        </Badge>
      </div>
      
      {/* Game Area */}
      <Card className="gaming-card relative h-96 lg:h-[500px] overflow-hidden bg-gradient-to-br from-gray-900 to-black">
        <div className="absolute inset-0 p-4">
          {targets.map((target) => (
            <button
              key={target.id}
              onClick={() => hitTarget(target.id)}
              className="absolute w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br from-red-500 to-red-700 rounded-full border-4 border-red-300 hover:scale-110 transition-transform duration-200 flex items-center justify-center animate-pulse"
              style={{
                left: `${target.x}%`,
                top: `${target.y}%`,
                transform: 'translate(-50%, -50%)'
              }}
            >
              <Target className="w-6 h-6 lg:w-8 lg:h-8 text-white" />
              <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 text-yellow-400 font-bold text-sm">
                {target.points}
              </div>
            </button>
          ))}
          
          {targets.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Zap className="w-8 h-8 mx-auto mb-2" />
                <div>Targets appearing...</div>
              </div>
            </div>
          )}
        </div>
      </Card>
      
      {/* Game Instructions */}
      <div className="mt-4 text-center text-gray-400 text-sm">
        Click the red targets as fast as you can! Faster clicks = bonus points
      </div>
    </div>
  );
};

export default TapChallenge;